import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../../components/common/AnimatedSection';

// 样式组件
const AlgorithmContainer = styled('div', {
  padding: '0',
  maxWidth: '100%',
});

// 算法流程展示区域
const ProcessSection = styled('div', {
  padding: '40px 0 80px',
  background: '#0A0F1A',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '50px',
    textAlign: 'center',
    color: '#121b33',
  },
});

// 算法流程步骤
const ProcessSteps = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '900px',
  margin: '0 auto',
  position: 'relative',
  padding: '0 20px',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '50%',
    width: '2px',
    backgroundColor: 'rgba(53, 99, 233, 0.2)',
    transform: 'translateX(-50%)',
    zIndex: 0,
    
    '@media (max-width: 768px)': {
      left: '30px',
      transform: 'none',
    },
  },
});

const ProcessStep = styled('div', {
  display: 'flex',
  position: 'relative',
  margin: '0 0 60px',
  zIndex: 1,
  
  '&:last-child': {
    marginBottom: 0,
  },
  
  '&:nth-child(odd)': {
    flexDirection: 'row',
    
    '@media (max-width: 768px)': {
      flexDirection: 'row',
    },
  },
  
  '&:nth-child(even)': {
    flexDirection: 'row-reverse',
    
    '@media (max-width: 768px)': {
      flexDirection: 'row',
    },
    
    '& .process-content': {
      alignItems: 'flex-end',
      textAlign: 'right',
      
      '@media (max-width: 768px)': {
        alignItems: 'flex-start',
        textAlign: 'left',
      },
    },
  },
});

const StepNumber = styled('div', {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #3563E9 0%, #6C5CE7 100%)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  fontWeight: '700',
  flexShrink: 0,
  boxShadow: '0 8px 20px rgba(53, 99, 233, 0.3)',
  zIndex: 2,
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  
  '@media (max-width: 768px)': {
    left: '30px',
    transform: 'none',
  },
});

const ProcessContent = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '30px',
  backgroundColor: 'white',
  borderRadius: '15px',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  margin: '0 60px',
  width: 'calc(50% - 90px)',
  alignItems: 'flex-start',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
  },
  
  '@media (max-width: 768px)': {
    width: 'auto',
    marginLeft: '60px',
    marginRight: '0',
  },
  
  '& h3': {
    fontSize: '22px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#121b33',
  },
  
  '& p': {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '15px',
  },
  
  '& ul': {
    paddingLeft: '20px',
    margin: '0 0 15px',
    
    '& li': {
      margin: '8px 0',
      color: '#666',
    },
  },
});

// 技术分析区域
const TechSection = styled('div', {
  background: '#f8fafc',
  padding: '80px 0',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '50px',
    textAlign: 'center',
    color: '#121b33',
  },
});

const TechCards = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '30px',
  maxWidth: '1100px',
  margin: '0 auto',
  padding: '0 20px',
});

const TechCard = styled('div', {
  background: 'white',
  borderRadius: '15px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  },
  
  '& .card-header': {
    background: 'linear-gradient(135deg, #3563E9 0%, #6C5CE7 100%)',
    color: 'white',
    padding: '20px 30px',
    
    '& h3': {
      fontSize: '22px',
      fontWeight: '600',
      margin: 0,
    },
  },
  
  '& .card-body': {
    padding: '30px',
    
    '& p': {
      fontSize: '16px',
      color: '#666',
      lineHeight: '1.6',
      marginBottom: '20px',
    },
    
    '& ul': {
      paddingLeft: '20px',
      margin: 0,
      
      '& li': {
        margin: '10px 0',
        color: '#666',
      },
    },
  },
});

// 算法应用区域
const ApplicationSection = styled('div', {
  padding: '80px 0',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '50px',
    textAlign: 'center',
    color: '#121b33',
  },
});

const ApplicationGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '30px',
  maxWidth: '1100px',
  margin: '0 auto',
  padding: '0 20px',
});

const ApplicationCard = styled('div', {
  background: 'white',
  borderRadius: '15px',
  padding: '30px',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  },
  
  '& .icon': {
    width: '70px',
    height: '70px',
    background: 'rgba(53, 99, 233, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
    marginBottom: '20px',
  },
  
  '& h3': {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#121b33',
  },
  
  '& p': {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.6',
  },
  
  '& .performance': {
    marginTop: '20px',
    fontSize: '16px',
    color: '#3563E9',
    fontWeight: '600',
  },
});

// AI算法页面组件
const AiAlgorithm: React.FC = () => {
  const { t } = useTranslation();
  
  // 算法流程步骤数据
  const processSteps = [
    {
      title: t('ai.algorithm.process.step1.title', '数据采集与处理'),
      description: t('ai.algorithm.process.step1.description', '实时收集市场价格、区块链网络难度、矿机状态等多维数据，通过数据清洗和归一化处理，为AI算法提供高质量的输入信息。'),
      details: [
        t('ai.algorithm.process.step1.detail1', '加密货币市场价格数据'),
        t('ai.algorithm.process.step1.detail2', '区块链网络难度变化'),
        t('ai.algorithm.process.step1.detail3', '矿机运行状态参数'),
      ],
    },
    {
      title: t('ai.algorithm.process.step2.title', '多维特征分析'),
      description: t('ai.algorithm.process.step2.description', '利用深度学习模型分析数据特征，识别市场走势、网络难度变化趋势，预测最佳挖矿时机和参数配置。'),
      details: [
        t('ai.algorithm.process.step2.detail1', '时序特征提取'),
        t('ai.algorithm.process.step2.detail2', '模式识别与分类'),
        t('ai.algorithm.process.step2.detail3', '异常检测与处理'),
      ],
    },
    {
      title: t('ai.algorithm.process.step3.title', '决策模型生成'),
      description: t('ai.algorithm.process.step3.description', '综合考虑收益最大化和能耗优化目标，构建多目标优化决策模型，生成最优挖矿策略。'),
      details: [
        t('ai.algorithm.process.step3.detail1', '收益风险评估'),
        t('ai.algorithm.process.step3.detail2', '多目标优化算法'),
        t('ai.algorithm.process.step3.detail3', '动态调整策略'),
      ],
    },
    {
      title: t('ai.algorithm.process.step4.title', '参数实时优化'),
      description: t('ai.algorithm.process.step4.description', '将决策模型的输出转化为具体的挖矿参数设置，实时调整矿机功率、算力分配和环境控制，实现收益最大化。'),
      details: [
        t('ai.algorithm.process.step4.detail1', '矿机功率动态调整'),
        t('ai.algorithm.process.step4.detail2', '算力智能分配'),
        t('ai.algorithm.process.step4.detail3', '环境参数控制'),
      ],
    },
    {
      title: t('ai.algorithm.process.step5.title', '反馈与持续学习'),
      description: t('ai.algorithm.process.step5.description', '持续监控挖矿结果，将实际收益与预期目标比对，不断优化算法模型，提高决策准确性。'),
      details: [
        t('ai.algorithm.process.step5.detail1', '结果评估与分析'),
        t('ai.algorithm.process.step5.detail2', '模型持续训练'),
        t('ai.algorithm.process.step5.detail3', '策略迭代优化'),
      ],
    },
  ];
  
  // 核心技术卡片数据
  const techCards = [
    {
      title: t('ai.algorithm.tech.card1.title', '深度强化学习'),
      description: t('ai.algorithm.tech.card1.description', '采用最新的深度强化学习算法，通过奖励机制指导AI模型学习最优挖矿策略，不断适应市场变化和网络难度调整。'),
      points: [
        t('ai.algorithm.tech.card1.point1', 'DQN (深度Q网络) 与市场决策'),
        t('ai.algorithm.tech.card1.point2', 'PPO (近端策略优化) 用于连续控制'),
        t('ai.algorithm.tech.card1.point3', '多智能体协作学习'),
      ],
    },
    {
      title: t('ai.algorithm.tech.card2.title', '时序预测模型'),
      description: t('ai.algorithm.tech.card2.description', '结合LSTM和Transformer架构的时序预测模型，精确预测加密货币价格走势和网络难度变化，为挖矿决策提供前瞻性指导。'),
      points: [
        t('ai.algorithm.tech.card2.point1', 'LSTM长短期记忆网络'),
        t('ai.algorithm.tech.card2.point2', 'Transformer注意力机制'),
        t('ai.algorithm.tech.card2.point3', '多变量时间序列分析'),
      ],
    },
    {
      title: t('ai.algorithm.tech.card3.title', '多目标优化'),
      description: t('ai.algorithm.tech.card3.description', '使用进化算法和粒子群优化等技术，同时优化收益最大化和能耗最小化等多个目标，找到最平衡的挖矿参数配置。'),
      points: [
        t('ai.algorithm.tech.card3.point1', 'Pareto最优解搜索'),
        t('ai.algorithm.tech.card3.point2', '约束条件下的优化'),
        t('ai.algorithm.tech.card3.point3', '动态权重调整策略'),
      ],
    },
  ];
  
  // 算法应用场景数据
  const applications = [
    {
      icon: "⚡",
      title: t('ai.algorithm.application.card1.title', '动态功率调整'),
      description: t('ai.algorithm.application.card1.description', '根据电价波动和挖矿收益预测，智能调整矿机功率，降低电力成本，提高挖矿利润。'),
      performance: t('ai.algorithm.application.card1.performance', '平均节省能耗 25%'),
    },
    {
      icon: "🔄",
      title: t('ai.algorithm.application.card2.title', '多币种智能切换'),
      description: t('ai.algorithm.application.card2.description', '分析多种加密货币的收益率，自动切换到最有利可图的币种，确保始终获得最高回报。'),
      performance: t('ai.algorithm.application.card2.performance', '收益提升高达 520%'),
    },
    {
      icon: "🛡️",
      title: t('ai.algorithm.application.card3.title', '设备健康监控'),
      description: t('ai.algorithm.application.card3.description', '持续监测矿机健康状态，预测潜在故障，实施预防性维护，降低停机风险。'),
      performance: t('ai.algorithm.application.card3.performance', '可用性提高到 99.9%'),
    },
    {
      icon: "🌡️",
      title: t('ai.algorithm.application.card4.title', '环境自适应优化'),
      description: t('ai.algorithm.application.card4.description', '根据环境温度变化和散热效率，自动调整矿场散热系统和运行参数，保持最佳工作状态。'),
      performance: t('ai.algorithm.application.card4.performance', '延长设备寿命 40%'),
    },
  ];
  
  return (
    <AlgorithmContainer>
      <ProcessSection>
        <AnimatedSection direction="up" delay={0.1}>
          <h2>{t('ai.algorithm.process.title', 'AI 挖矿算法流程')}</h2>
          
          <ProcessSteps>
            {processSteps.map((step, index) => (
              <ProcessStep key={index}>
                <StepNumber>{index + 1}</StepNumber>
                <ProcessContent className="process-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                  <ul>
                    {step.details.map((detail, i) => (
                      <li key={i}>{detail}</li>
                    ))}
                  </ul>
                </ProcessContent>
              </ProcessStep>
            ))}
          </ProcessSteps>
        </AnimatedSection>
      </ProcessSection>
      
      <TechSection>
        <AnimatedSection direction="up" delay={0.2}>
          <h2>{t('ai.algorithm.tech.title', '核心技术')}</h2>
          
          <TechCards>
            {techCards.map((card, index) => (
              <TechCard key={index}>
                <div className="card-header">
                  <h3>{card.title}</h3>
                </div>
                <div className="card-body">
                  <p>{card.description}</p>
                  <ul>
                    {card.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              </TechCard>
            ))}
          </TechCards>
        </AnimatedSection>
      </TechSection>
      
      <ApplicationSection>
        <AnimatedSection direction="up" delay={0.3}>
          <h2>{t('ai.algorithm.application.title', 'AI 算法应用场景')}</h2>
          
          <ApplicationGrid>
            {applications.map((app, index) => (
              <ApplicationCard key={index}>
                <div className="icon">{app.icon}</div>
                <h3>{app.title}</h3>
                <p>{app.description}</p>
                <div className="performance">{app.performance}</div>
              </ApplicationCard>
            ))}
          </ApplicationGrid>
        </AnimatedSection>
      </ApplicationSection>
    </AlgorithmContainer>
  );
};

export default AiAlgorithm; 